import { useEffect, useState } from "preact/hooks";

type Selection = { category: string; subcategory: string };

const ALL: Selection = { category: "all", subcategory: "all" };

/** Keeps the choice in the address bar (`?category=&subcategory=`) without adding a history entry. */
const writeToUrl = ({ category, subcategory }: Selection): void => {
  const url = new URL(window.location.href);
  for (const [key, value] of [
    ["category", category],
    ["subcategory", subcategory],
  ] as const) {
    if (value !== "all") {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
  }
  window.history.replaceState({}, "", url.toString());
};

const readFromUrl = (): Selection => {
  const params = new URLSearchParams(window.location.search);
  return { category: params.get("category") ?? "all", subcategory: params.get("subcategory") ?? "all" };
};

export type CategoryFilterState = Selection & {
  selectCategory: (category: string) => void;
  selectSubcategory: (subcategory: string) => void;
};

/**
 * The category and subcategory an explore page is filtered by. It starts as "all", which is what the page was
 * prerendered with, and takes the address bar's choice once mounted, so hydration always matches the prerender.
 */
export const useCategoryFilter = (): CategoryFilterState => {
  const [selection, setSelection] = useState<Selection>(ALL);

  useEffect(() => {
    const fromUrl = readFromUrl();
    if (fromUrl.category !== "all" || fromUrl.subcategory !== "all") {
      setSelection(fromUrl);
    }
  }, []);

  const choose = (next: Selection) => {
    setSelection(next);
    writeToUrl(next);
  };

  return {
    ...selection,
    selectCategory: (category) => choose({ category, subcategory: "all" }),
    selectSubcategory: (subcategory) => choose({ category: selection.category, subcategory }),
  };
};

/** The search filter of a selection: "all" means no filter. */
export const filterOf = ({ category, subcategory }: Selection): { category?: string; subcategory?: string } => ({
  category: category === "all" ? undefined : category,
  subcategory: subcategory === "all" ? undefined : subcategory,
});
